import React, { useContext, useEffect, useState } from 'react'
import { Dialog, DialogTitle, DialogContent, Typography, Alert, Button, Box, CircularProgress, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip, DialogActions, IconButton } from '@mui/material'
import { OrderType } from '../../interfaces/delivery-company/orders'
import { AuthContext } from '../../contexts/user-context/user-context'
import CustomDialog from './custom-dialog/custom-dialog'
import ChecklistIcon from '@mui/icons-material/Checklist';
import axios, { AxiosError, AxiosResponse } from 'axios'
import { API_ENDPOINT } from '../../configurations/global.config'
import { SnackBarProvider, useSnackBar } from '../../contexts/snackbar'
import { ErrorsFlattenner } from './errors-flattenner/errors-flattenner'
import GenericTable from './generic-table/generic-table'
import CheckIcon from '@mui/icons-material/Check';
import { PrintWaybillsMultiSizes } from './bulk-action/print-waybills'
import { isMobile, isTablet } from 'react-device-detect'
import PrintIcon from '@mui/icons-material/Print';

interface RequestPickupDialogProps {
  orders: OrderType[]
  onClose: () => void
  open: boolean
}

type PickupResponse =Record<string, {created: boolean, task_id: number }> 

// Interface for the table data
interface PickupTaskResult {
  taskId: number
  isNew: boolean
  orderCount: number,
  orders: string[]
}

function RequestPickupDialog({ orders, onClose, open }: RequestPickupDialogProps) {
    const {config} = useContext(AuthContext)
    const [loading, setLoading] = useState(false)
    const [errorResponse, setErrorResponse] = useState<AxiosResponse>()
    const [result, setResult] = useState<PickupResponse>()
    const {showMessage} = useSnackBar()

    const [isPrintingWaybillsForOrderIds, setIsPrintingWaybillsForOrderIds] = useState<string[]>()

    const [awaitingUserConfirmation, setAwaitingUserConfirmation] = useState(true)

    useEffect(() => {
        setLoading(false)
        setErrorResponse(undefined)
        setResult(undefined)
        setAwaitingUserConfirmation(true)
    }, [open])

    const handleRequestPickup = async () => {
        setLoading(true)
        setAwaitingUserConfirmation(false)
        try {
            const response = await axios.post<PickupResponse>(`${API_ENDPOINT}/v1/orders/request-pickup/`, {
                orders: orders.map(order => order.id)
            }, config)
            setResult(response.data)
        } catch (error: any) {
            showMessage('Failed to request pickup', 'error')
            if (axios.isAxiosError(error)) {
                
                setErrorResponse(error.response as AxiosResponse)
            }
            console.error(error)
        } finally {
            setLoading(false)
        }
    }

    // Transform result data to group by task_id
    const getTableData = (): PickupTaskResult[] => {
        if (!result) return []
        
        const taskGroups = new Map<number, { created: boolean, orderCount: number, orders: string[] }>()
        
        Object.entries(result).forEach(([orderId, { created, task_id }]) => {
            if (taskGroups.has(task_id)) {
                const existing = taskGroups.get(task_id)!
                taskGroups.set(task_id, {
                    created: existing.created || created,
                    orderCount: existing.orderCount + 1,
                    orders: [...existing.orders, orderId]
                })
            } else {
                taskGroups.set(task_id, {
                    created,
                    orderCount: 1,
                    orders: [orderId]
                })
            }
        })
        
        return Array.from(taskGroups.entries()).map(([taskId, { created, orderCount, orders }]) => ({
            taskId,
            isNew: created,
            orderCount,
            orders
        }))
    }

    
    return (
        <>
        <CustomDialog open={open} onClose={onClose} maxWidth='md' fullWidth>
            <DialogTitle><Typography variant='h4' align='center'>Request Pickup</Typography></DialogTitle>
            <DialogContent>
                {
                    awaitingUserConfirmation && <>
                        <Alert severity='info' 
                        sx={{maxWidth: '500px', margin: '0 auto', textAlign: 'center'}}
                            icon={<ChecklistIcon />}
                        >
                            You are about to request pickup for the <b>{orders.length}</b> selected orders.
                            <br />
                            Are you sure you want to proceed? 
                        </Alert>
                        
                        <Box display='flex' justifyContent='center' mt={2} gap={2}>
                                <Button variant='outlined' color='error' onClick={onClose}>Cancel</Button>
                                <Button variant='outlined' color='primary' onClick={handleRequestPickup} disabled={loading}>Proceed</Button>
                        </Box>     
                    </>
                }
                

                {
                    loading ? <Box display='flex' justifyContent='center' mt={2} gap={2}>
                        <CircularProgress />
                        <Typography variant='body1'>Requesting pickup...</Typography>
                    </Box> : <>

                        {errorResponse && <Alert severity='error'><ErrorsFlattenner rawErrors={errorResponse.data} /></Alert>}

                        {result && (
                            <Box mt={2}>
                                <Typography variant='h6' gutterBottom>Results</Typography>
                                <GenericTable<PickupTaskResult> 
                                        sourceView="request-pickup-dialog"
                                        items={getTableData()}
                                        fullWidth={true}
                                        columns={[
                                            {
                                                header: 'Pickup Task ID',
                                                id: "taskId",
                                                display_body: (row) => row.taskId.toString()
                                            },
                                            {
                                                header: 'Status',
                                                id: "status",
                                                display_body: (row) => row.isNew ? <Chip icon={<CheckIcon />} label='Created new request' color='success' /> : <Chip icon={<CheckIcon />} label='Updated existing request' color='warning' />
                                            },
                                            {
                                                header: 'Orders',
                                                id: "orderCount",
                                                display_body: (row) => <>
                                                {row.orderCount} {row.orderCount > 1 ? 'orders' : 'order'}
                                                <IconButton onClick={()=>setIsPrintingWaybillsForOrderIds(row.orders)}>
                                                    <PrintIcon />
                                                </IconButton>
                                                </>
                                            }
                                        ]} 
                                        selectedItems={new Map()}
                                        isLoading={false} 
                                        getItemUID={function (item: PickupTaskResult): string {
                                            return item.taskId.toString()
                                        } } 
                                />

                            </Box>
                        )}
                    </> 
                }

                
            </DialogContent>

        </CustomDialog>

        {
                isPrintingWaybillsForOrderIds && <CustomDialog fullWidth open={true} onClose={()=>setIsPrintingWaybillsForOrderIds(undefined)}>
                <DialogContent>
                  <br />
                  <PrintWaybillsMultiSizes 
                            onClose={() => setIsPrintingWaybillsForOrderIds(undefined)}
                            orders={isPrintingWaybillsForOrderIds.map(o=>{return {id: o} as OrderType})}           
                 />
                </DialogContent>
              </CustomDialog>
            }

    </>
    )
}

export default RequestPickupDialog